import { GOOGLE_OAUTH_CLIENT_ID, MICROSOFT_OAUTH_CLIENT_ID } from '../constants';

// OAuth Configuration
export const OAUTH_CONFIG = {
    google: {
        clientId: GOOGLE_OAUTH_CLIENT_ID || '',
        redirectUri: `${window.location.origin}/oauth/callback/google`,
        scope: 'profile email',
        authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
        responseType: 'token',
    },
    microsoft: {
        clientId: MICROSOFT_OAUTH_CLIENT_ID || '',
        redirectUri: `${window.location.origin}/oauth/callback/microsoft`,
        scope: 'User.Read email',
        authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
        responseType: 'token',
    },
};

/**
 * Generate OAuth authorization URL
 * @param {string} provider - OAuth provider (google or microsoft)
 * @returns {string} Authorization URL
 */
export const getOAuthUrl = (provider) => {
    const config = OAUTH_CONFIG[provider];
    
    if (!config || !config.clientId) {
        console.error(`OAuth configuration for ${provider} is missing or incomplete`);
        return null;
    }
    
    const params = new URLSearchParams({
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        response_type: config.responseType,
        scope: config.scope,
        prompt: 'select_account',
    });
    
    return `${config.authUrl}?${params.toString()}`;
};

/**
 * Initiate OAuth flow by redirecting to provider
 * @param {string} provider - OAuth provider (google or microsoft)
 */
export const initiateOAuthFlow = (provider) => {
    const url = getOAuthUrl(provider);
    
    if (!url) {
        throw new Error(`Failed to generate OAuth URL for ${provider}`);
    }
    
    // Store the current path to redirect back after OAuth
    // But don't store /login or /register - always go to /chat after OAuth
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/register') {
        sessionStorage.setItem('oauth_redirect_path', currentPath);
    } else {
        // Clear any stored path from previous sessions
        sessionStorage.removeItem('oauth_redirect_path');
    }
    
    // Redirect to OAuth provider
    window.location.href = url;
};

/**
 * Parse OAuth callback hash parameters
 * @returns {Object} Parsed parameters from URL hash
 */
export const parseOAuthCallback = () => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    
    return {
        accessToken: params.get('access_token'),
        tokenType: params.get('token_type'),
        expiresIn: params.get('expires_in'),
        error: params.get('error'),
        errorDescription: params.get('error_description'),
    };
};

/**
 * Handle OAuth callback and authenticate with backend
 * @param {string} provider - OAuth provider (google or microsoft)
 * @param {string} accessToken - Access token from OAuth provider
 * @param {Function} apiCall - API call function
 * @returns {Promise} Backend authentication response
 */
export const handleOAuthCallback = async (provider, accessToken, apiCall) => {
    try {
        const endpoint = provider === 'google' ? '/api/auth/google/' : '/api/auth/microsoft/';
        
        const response = await apiCall.post(endpoint, {
            access_token: accessToken,
        });
        
        return response.data;
    } catch (error) {
        console.error(`OAuth ${provider} authentication failed:`, error);
        throw error;
    }
};

